import { useState, useEffect } from 'react';
import { useAuthStore } from '../hooks/useAuth';
import { OperationsFeatureGate, isOperationsTierUser } from '../components/entitlements/OperationsFeatureGate';
import { DockDetailPanel } from '../components/analytics/DockDetailPanel';


interface AnalyticsData {
    current_stats: {
        avg_dwell_time_mins: number;
        avg_wait_time_mins: number;
        carrier_reliability_score: number | null;
        reliability_sample_count?: number;
        active_docks: number;
        total_docks: number;
    };
    historical_7d_averages: {
        avg_dwell_time_mins: number;
        avg_wait_time_mins: number;
        carrier_reliability_score: number | null;
    };
    dock_states: {
        id: string;
        dockNumber?: string;
        status: 'occupied' | 'idle' | 'turnaround';
        last_event: string;
        carrier_reliability?: number;
        carrier_mc?: string;
        load_complexity?: number;
        historical_performance?: number;
        active_users?: number;
    }[];
    metrics: {
        avgDwellTime: number;
        avgWaitTime: number;
        onTimePercentage: number;
    };
    dailyVolume: {
        date: string;
        count: number;
    }[];
    carrierStats: {
        name: string;
        totalAppointments: number;
        onTimePct: number | null;
        avgDwell: number | null;
    }[];
    carrierStatsAllTime?: {
        name: string;
        totalAppointments: number;
        onTimePct: number | null;
        avgDwell: number | null;
    }[];
}

// --- Formatting Helpers ---
function fmtMinutes(mins: number | null | undefined): string {
    if (mins === null || mins === undefined || isNaN(mins)) return '—';
    if (mins < 60) return `${Math.round(mins)}m`;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function fmtPct(value: number | null | undefined, denominator?: number): string {
    if (value === null || value === undefined || isNaN(value)) return '—';
    if (denominator !== undefined && denominator === 0) return '—';
    return `${Math.round(value)}%`;
}

// Format ISO YYYY-MM-DD to short display label based on count of data points
function fmtChartDate(iso: string, total: number): string {
    // Try to parse as ISO date
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const d = new Date(iso + 'T12:00:00'); // noon to avoid TZ shift
        if (total <= 14) return d.toLocaleDateString('en-US', { weekday: 'short' }); // Mon, Tue
        return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }); // 2/15
    }
    return iso; // Fallback: already formatted
}

function computeDelta(current: number, previous: number): { text: string; type: 'positive' | 'negative' | 'neutral' } {
    if (!previous || isNaN(previous) || isNaN(current)) return { text: '—', type: 'neutral' };
    const diff = current - previous;
    const pct = Math.round((diff / previous) * 100);
    if (pct === 0) return { text: 'No change', type: 'neutral' };
    return {
        text: `${pct > 0 ? '+' : ''}${pct}% vs prior`,
        type: pct > 0 ? 'positive' : 'negative',
    };
}

// For dwell/wait: lower is better, so invert the delta type
function computeDeltaInverse(current: number, previous: number): { text: string; type: 'positive' | 'negative' | 'neutral' } {
    const d = computeDelta(current, previous);
    if (d.type === 'positive') return { ...d, type: 'negative' };
    if (d.type === 'negative') return { ...d, type: 'positive' };
    return d;
}

export function Analytics() {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedDockId, setSelectedDockId] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState('7d');
    const [kpiModal, setKpiModal] = useState<{ title: string; explanation: string; items: string[] } | null>(null);
    const [carrierView, setCarrierView] = useState<'range' | 'alltime'>('range');

    useEffect(() => {
        if (token && isOperationsEntitled) fetchAnalytics();
        else setLoading(false);
    }, [token, dateRange]);

    const fetchAnalytics = async () => {
        if (!token || !isOperationsEntitled) return;
        try {
            setLoading(true);
            setError(null);
            if (import.meta.env.DEV) await new Promise(r => setTimeout(r, 300));

            // Wire dateRange as query param; custom falls back to 7d
            const range = dateRange === 'custom' ? '7d' : dateRange;
            const res = await fetch(`/api/analytics/summary?range=${range}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!res.ok) {
                if (res.status === 404 && import.meta.env.DEV) { loadDemoData(); return; }
                throw new Error(`Server Error (${res.status})`);
            }

            const analyticsData = await res.json();
            if (!analyticsData.current_stats || !analyticsData.dock_states) {
                if (import.meta.env.DEV) { loadDemoData(); return; }
                throw new Error('Invalid analytics response');
            }
            setData(analyticsData);

        } catch (err: any) {
            console.error('Failed to load analytics:', err);
            if (import.meta.env.DEV) { loadDemoData(); } else { setError(err.message || 'Analytics unavailable'); }
        } finally {
            setLoading(false);
        }
    };

    const loadDemoData = () => {
        setData({
            current_stats: {
                avg_dwell_time_mins: 142,
                avg_wait_time_mins: 18,
                carrier_reliability_score: 0.82,
                active_docks: 12,
                total_docks: 15
            },
            historical_7d_averages: {
                avg_dwell_time_mins: 115,
                avg_wait_time_mins: 22,
                carrier_reliability_score: 0.88
            },
            dock_states: [
                { id: "D-101", status: "occupied", last_event: new Date(Date.now() - 1000 * 60 * 45).toISOString(), carrier_reliability: 0.95, carrier_mc: "123456" },
                { id: "D-102", status: "idle", last_event: new Date(Date.now() - 1000 * 60 * 120).toISOString(), carrier_reliability: 1.0 },
                { id: "D-103", status: "turnaround", last_event: new Date(Date.now() - 1000 * 60 * 12).toISOString(), carrier_reliability: 0.88 },
                { id: "D-104", status: "occupied", last_event: new Date(Date.now() - 1000 * 60 * 85).toISOString(), carrier_reliability: 0.75, carrier_mc: "987654" },
                { id: "D-105", status: "occupied", last_event: new Date(Date.now() - 1000 * 60 * 92).toISOString(), carrier_reliability: 0.92, carrier_mc: "112233" },
                { id: "D-106", status: "idle", last_event: new Date(Date.now() - 1000 * 60 * 300).toISOString() },
                { id: "D-107", status: "idle", last_event: new Date(Date.now() - 1000 * 60 * 400).toISOString() },
                { id: "D-108", status: "turnaround", last_event: new Date(Date.now() - 1000 * 60 * 25).toISOString(), carrier_reliability: 0.65 },
            ],
            metrics: { avgDwellTime: 45, avgWaitTime: 12, onTimePercentage: 94 },
            dailyVolume: [
                { date: 'Mon', count: 12 }, { date: 'Tue', count: 19 }, { date: 'Wed', count: 15 },
                { date: 'Thu', count: 22 }, { date: 'Fri', count: 28 }, { date: 'Sat', count: 8 }, { date: 'Sun', count: 5 }
            ],
            carrierStats: [
                { name: 'JB Hunt', totalAppointments: 45, onTimePct: 98, avgDwell: 38 },
                { name: 'TFI International', totalAppointments: 32, onTimePct: 92, avgDwell: 45 },
                { name: 'Schneider', totalAppointments: 28, onTimePct: 89, avgDwell: 52 },
                { name: 'XPO', totalAppointments: 24, onTimePct: 85, avgDwell: 58 },
            ]
        });
    };

    // --- Error State (production) ---
    if (!isOperationsEntitled) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
                <OperationsFeatureGate
                    featureName="Efficiency Report & Carrier Reliability Analytics"
                    description="Historical dwell & wait-time trends, carrier reliability scoring, and per-dock turnaround efficiency reporting are included in the BayWhiz Operations plan ($199 USD/month per facility). Your Starter plan ($99 USD/month) includes full dock scheduling, live schedule updates, gate execution, and CSV schedule exports."
                    highlights={[
                        '7-day, 14-day, and 30-day dock turnaround & wait-time benchmarks',
                        'Weighted carrier reliability scorecards and on-time delivery rankings',
                        'Daily appointment throughput and dock utilization trend analysis',
                    ]}
                    testId="analytics-efficiency-operations-gate"
                />
            </div>
        );
    }

    if (!loading && error && !data) {
        return (
            <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                <div className="text-center space-y-4">
                    <span className="text-4xl">📊</span>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">Analytics Unavailable</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">{error}. Try again or expand the date range.</p>
                    <button onClick={fetchAnalytics} className="px-4 py-2 text-sm rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition-colors">Retry</button>
                </div>
            </div>
        );
    }

    // --- Loading Skeleton ---
    if (loading || !data) {
        return (
            <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-950 p-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Header skeleton */}
                    <div className="h-8 w-48 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
                    <div className="h-4 w-64 bg-slate-100 dark:bg-slate-800/60 rounded animate-pulse" />
                    {/* KPI row skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 h-28 animate-pulse">
                                <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-4" />
                                <div className="h-7 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                                <div className="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded" />
                            </div>
                        ))}
                    </div>
                    {/* Chart skeleton */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 h-80 animate-pulse" />
                        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 h-80 animate-pulse" />
                    </div>
                    {/* Bottom row skeleton */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 h-56 animate-pulse" />
                        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 h-56 animate-pulse" />
                    </div>
                </div>
            </div>
        );
    }

    // Throughput total
    const throughputTotal = data.dailyVolume.reduce((acc, d) => acc + d.count, 0);

    // Dock utilization from real data
    const totalDocks = data.current_stats.total_docks || data.dock_states.length || 1;
    const dockCounts = {
        idle: data.dock_states.filter(d => d.status === 'idle').length,
        scheduled: 0,
        inUse: data.dock_states.filter(d => d.status === 'occupied').length,
        delayed: data.dock_states.filter(d => d.status === 'turnaround').length,
    };
    const reportedSum = dockCounts.idle + dockCounts.scheduled + dockCounts.inUse + dockCounts.delayed;
    const unreported = Math.max(0, totalDocks - reportedSum);

    // KPI deltas — guard against small samples
    const hist = data.historical_7d_averages;
    const curr = data.current_stats;
    const sampleCount = curr.reliability_sample_count ?? 0;
    const hasEnoughSamples = throughputTotal >= 5;
    const dwellDelta = hasEnoughSamples
        ? computeDeltaInverse(curr.avg_dwell_time_mins, hist.avg_dwell_time_mins)
        : { text: 'Limited data', type: 'neutral' as const };
    const waitDelta = hasEnoughSamples
        ? computeDeltaInverse(curr.avg_wait_time_mins, hist.avg_wait_time_mins)
        : { text: 'Limited data', type: 'neutral' as const };
    const reliabilityDelta = curr.carrier_reliability_score !== null && sampleCount >= 5 && hist.carrier_reliability_score !== null
        ? computeDelta(curr.carrier_reliability_score * 100, hist.carrier_reliability_score * 100)
        : { text: sampleCount < 5 ? 'Limited data' : '—', type: 'neutral' as const };

    // Active carriers with nonzero loads (for selected view)
    const rangeLabel = dateRange === '30d' ? 'Last 30 Days' : dateRange === '14d' ? 'Last 14 Days' : 'Last 7 Days';
    const activeCarriers = data.carrierStats.filter(c => c.totalAppointments > 0);
    const allTimeCarriers = (data.carrierStatsAllTime || []).filter(c => c.totalAppointments > 0);
    const displayedCarriers = carrierView === 'alltime' ? allTimeCarriers : activeCarriers;

    return (
        <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 transition-colors duration-300">
            {/* Slide-Over Panel */}
            <DockDetailPanel dockId={selectedDockId} onClose={() => setSelectedDockId(null)} />

            {/* KPI Drill-down Modal */}
            {kpiModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setKpiModal(null)} />
                    <div onClick={e => e.stopPropagation()} className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">{kpiModal.title}</h3>
                            <button onClick={() => setKpiModal(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xl leading-none">&times;</button>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <p className="text-sm text-slate-600 dark:text-slate-400">{kpiModal.explanation}</p>
                            {kpiModal.items.length > 0 ? (
                                <div>
                                    <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Top Contributors</h4>
                                    <ul className="space-y-1.5">
                                        {kpiModal.items.map((item, i) => (
                                            <li key={i} className="text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                                <span className="text-xs text-slate-400 font-mono w-4">{i + 1}.</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 italic">No data for selected range.</p>
                            )}
                        </div>
                        <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                            <button onClick={() => setKpiModal(null)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto p-6 space-y-6">

                {/* 1. HEADER */}
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">Efficiency Report</h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm">Performance summary for your warehouse</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 hidden md:block">Range:</label>
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-50 text-sm rounded-md px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        >
                            <option value="7d">Last 7 Days</option>
                            <option value="14d">Last 14 Days</option>
                            <option value="30d">Last 30 Days</option>
                            <option value="custom" disabled>Custom Range (Coming soon)</option>
                        </select>
                    </div>
                </header>

                {/* 2. KPI CARDS */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KpiCard
                        title="Avg Dwell Time"
                        value={fmtMinutes(curr.avg_dwell_time_mins)}
                        delta={dwellDelta.text}
                        deltaType={dwellDelta.type}
                        icon="⏱️"
                        onClick={() => setKpiModal({
                            title: 'Avg Dwell Time',
                            explanation: `Average time trucks spend at a dock from check-in to check-out. Current: ${fmtMinutes(curr.avg_dwell_time_mins)}, Previous period: ${fmtMinutes(hist.avg_dwell_time_mins)}. Lower is better. Outliers over 24 hours are excluded.`,
                            items: activeCarriers.slice(0, 5).map(c => `${c.name}: ${fmtMinutes(c.avgDwell)} avg dwell (${c.totalAppointments} appts)`),
                        })}
                    />
                    <KpiCard
                        title="Avg Wait Time"
                        value={fmtMinutes(curr.avg_wait_time_mins)}
                        delta={waitDelta.text}
                        deltaType={waitDelta.type}
                        icon="🚧"
                        onClick={() => setKpiModal({
                            title: 'Avg Wait Time',
                            explanation: `Average time from scheduled arrival to dock check-in. Current: ${fmtMinutes(curr.avg_wait_time_mins)}, Previous period: ${fmtMinutes(hist.avg_wait_time_mins)}. Lower is better.`,
                            items: [],
                        })}
                    />
                    <KpiCard
                        title="Carrier Reliability"
                        value={fmtPct(curr.carrier_reliability_score !== null ? curr.carrier_reliability_score * 100 : null)}
                        delta={reliabilityDelta.text}
                        deltaType={reliabilityDelta.type}
                        icon="🛡️"
                        onClick={() => setKpiModal({
                            title: 'Carrier Reliability',
                            explanation: `Percentage of appointments completed on time (${sampleCount} evaluated). ${sampleCount < 5 ? 'Requires at least 5 appointments for a confident score.' : `Current: ${fmtPct(curr.carrier_reliability_score !== null ? curr.carrier_reliability_score * 100 : null)}.`} Higher is better.`,
                            items: activeCarriers.slice(0, 5).map(c => `${c.name}: ${fmtPct(c.onTimePct)} on-time (${c.totalAppointments} appts)`),
                        })}
                    />
                    <KpiCard
                        title="Completed Appointments"
                        value={`${throughputTotal}`}
                        delta={rangeLabel}
                        deltaType="neutral"
                        icon="📦"
                        onClick={() => setKpiModal({
                            title: 'Completed Appointments',
                            explanation: `Total appointments completed (checked out) over the selected date range.`,
                            items: data.dailyVolume.map(d => `${fmtChartDate(d.date, data.dailyVolume.length)}: ${d.count} completed`),
                        })}
                    />
                </div>

                {/* 3. MAIN GRID */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Completed Appointments Chart */}
                    <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Completed Appointments</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Appointments checked out over time</p>
                        </div>
                        <div className="h-64 w-full">
                            <ResponsiveLineChart data={data.dailyVolume} />
                        </div>
                    </div>

                    {/* Right: Dock Status (real-time) */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm flex flex-col">
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Current Dock Status</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Real-time gate snapshot</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3 flex-1">
                            <StatusTile label="Idle" count={dockCounts.idle} color="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" />
                            <StatusTile label="In-Use" count={dockCounts.inUse} color="bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-200" />
                            <StatusTile label="Turnaround" count={dockCounts.delayed} color="bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-200" />
                            <StatusTile label="Total" count={totalDocks} color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-200" />
                        </div>
                        {unreported > 0 && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 text-center">Reporting {reportedSum} of {totalDocks} docks &middot; {unreported} unreported</p>
                        )}
                    </div>
                </div>

                {/* 4. BOTTOM ROW */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Top Carriers Table */}
                    <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm overflow-hidden">
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Top Carriers</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Performance leaderboard based on reliability</p>
                            </div>
                            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                                <button
                                    onClick={() => setCarrierView('range')}
                                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${carrierView === 'range' ? 'bg-cyan-600 text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                >
                                    {rangeLabel.replace('Last ', '')}
                                </button>
                                <button
                                    onClick={() => setCarrierView('alltime')}
                                    className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200 dark:border-slate-700 ${carrierView === 'alltime' ? 'bg-cyan-600 text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                >
                                    All Time
                                </button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            {displayedCarriers.length > 0 ? (
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-800">
                                        <tr>
                                            <th className="px-4 py-3 font-semibold rounded-l-lg">Carrier</th>
                                            <th className="px-4 py-3 font-semibold">Loads</th>
                                            <th className="px-4 py-3 font-semibold">On-Time %</th>
                                            <th className="px-4 py-3 font-semibold rounded-r-lg text-right">Avg Dwell</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {displayedCarriers.map((carrier, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                                <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-200">{carrier.name}</td>
                                                <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{carrier.totalAppointments}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${carrier.onTimePct === null ? 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' :
                                                        carrier.onTimePct >= 95 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400' :
                                                            carrier.onTimePct >= 85 ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400' :
                                                                'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                                                        }`}>
                                                        {fmtPct(carrier.onTimePct)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">{fmtMinutes(carrier.avgDwell)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="p-8 text-center text-slate-400 dark:text-slate-500">
                                    <span className="text-2xl block mb-2">📭</span>
                                    <p className="text-sm font-medium">No carrier activity in this period.</p>
                                    <p className="text-xs mt-1">Try expanding the date range to see carrier performance data.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Current Dock Status (bottom) */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Dock Activity</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Click a dock for details</p>
                        </div>
                        <div className="space-y-2">
                            {data.dock_states.length > 0 ? (
                                data.dock_states.map((dock) => (
                                    <button
                                        key={dock.id}
                                        onClick={() => setSelectedDockId(dock.id)}
                                        className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                                                dock.status === 'occupied' ? 'bg-emerald-500' :
                                                dock.status === 'turnaround' ? 'bg-amber-500' : 'bg-slate-300'
                                            }`} />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Dock {dock.dockNumber || dock.id}</span>
                                        </div>
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                            dock.status === 'occupied' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400' :
                                            dock.status === 'turnaround' ? 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400' :
                                            'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                                        }`}>{dock.status}</span>
                                    </button>
                                ))
                            ) : (
                                <p className="text-sm text-slate-400 text-center py-4">No docks configured</p>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

// --- Sub-Components ---

function KpiCard({ title, value, delta, deltaType, icon, onClick }: { title: string, value: string, delta: string, deltaType: 'positive' | 'negative' | 'neutral', icon: string, onClick?: () => void }) {
    return (
        <div
            onClick={onClick}
            className={`bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between h-full hover:border-slate-300 dark:hover:border-slate-700 transition-colors group ${onClick ? 'cursor-pointer hover:shadow-md' : ''}`}
        >
            <div className="flex justify-between items-start mb-2">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</h3>
                <span className="text-xl text-slate-300 group-hover:text-slate-500 transition-colors grayscale group-hover:grayscale-0">{icon}</span>
            </div>
            <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-50 tracking-tight">{value}</div>
                <div className={`text-xs font-medium mt-1 flex items-center gap-1
                    ${deltaType === 'positive' ? 'text-emerald-600 dark:text-emerald-400' :
                        deltaType === 'negative' ? 'text-red-600 dark:text-red-400' :
                            'text-slate-500 dark:text-slate-500'}`}>
                    {deltaType === 'positive' && '↑'}
                    {deltaType === 'negative' && '↓'}
                    {delta}
                </div>
            </div>
        </div>
    );
}

function StatusTile({ label, count, color }: { label: string, count: number, color: string }) {
    return (
        <div className={`p-3 rounded-lg flex flex-col items-center justify-center text-center ${color}`}>
            <span className="text-2xl font-bold mb-1">{count}</span>
            <span className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</span>
        </div>
    );
}

function ResponsiveLineChart({ data }: { data: { date: string; count: number }[] }) {
    if (!data || data.length === 0) return (
        <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <span className="text-2xl text-slate-200 mb-2">📉</span>
            <span className="text-sm font-medium">No data in this range</span>
            <span className="text-xs mt-1">Try expanding the date range.</span>
        </div>
    );

    const height = 100;
    const width = 300;
    const padLeft = 25; // Extra left padding for y-axis
    const padRight = 5;
    const padTop = 5;
    const padBottom = 5;
    const max = Math.max(...data.map(d => d.count), 5);
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;

    // Create points (guard: single data point uses midpoint X)
    const points = data.map((d, i) => {
        const x = data.length === 1 ? padLeft + chartW / 2 : (i / (data.length - 1)) * chartW + padLeft;
        const y = height - (d.count / max) * chartH - padBottom;
        return `${x},${y}`;
    }).join(' ');

    // Y-axis ticks (0, mid, max)
    const yTicks = [0, Math.round(max / 2), max];

    return (
        <div className="w-full h-full relative">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none">
                {/* Gridlines */}
                {yTicks.map((tick, i) => {
                    const y = height - (tick / max) * chartH - padBottom;
                    return (
                        <line key={i} x1={padLeft} x2={width - padRight} y1={y} y2={y}
                            stroke="currentColor" className="text-slate-200 dark:text-slate-800" strokeWidth="0.5"
                            vectorEffect="non-scaling-stroke" />
                    );
                })}

                {/* Y-axis labels */}
                {yTicks.map((tick, i) => {
                    const y = height - (tick / max) * chartH - padBottom;
                    return (
                        <text key={i} x={padLeft - 3} y={y + 1} textAnchor="end"
                            className="fill-slate-400 dark:fill-slate-500"
                            style={{ fontSize: '6px', fontFamily: 'ui-monospace, monospace' }}
                        >
                            {tick}
                        </text>
                    );
                })}

                {/* Area fill */}
                <path
                    d={`M ${padLeft} ${height - padBottom} L ${points} L ${width - padRight} ${height - padBottom} Z`}
                    className="fill-cyan-500/8 dark:fill-cyan-400/5"
                />

                {/* Line */}
                <polyline fill="none" stroke="#06b6d4" strokeWidth="2.5" points={points}
                    vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />

                {/* Data points + tooltips */}
                {data.map((d, i) => {
                    const x = data.length === 1 ? padLeft + chartW / 2 : (i / (data.length - 1)) * chartW + padLeft;
                    const y = height - (d.count / max) * chartH - padBottom;
                    return (
                        <g key={i} className="group">
                            {/* Invisible hitbox */}
                            <rect x={x - 12} y={0} width={24} height={height} fill="transparent" className="cursor-pointer" />
                            {/* Hover line */}
                            <line x1={x} x2={x} y1={padTop} y2={height - padBottom}
                                className="text-cyan-500/20 hidden group-hover:block" stroke="currentColor"
                                strokeWidth="1" vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                            {/* Dot */}
                            <circle cx={x} cy={y} r="3.5"
                                className="fill-white dark:fill-slate-900 stroke-cyan-500 stroke-2 hidden group-hover:block"
                                vectorEffect="non-scaling-stroke" />
                            {/* Tooltip */}
                            <foreignObject x={x - 25} y={y - 32} width="50" height="28" className="hidden group-hover:block pointer-events-none overflow-visible">
                                <div className="bg-slate-800 text-white text-[10px] px-2 py-1 rounded text-center shadow-lg font-mono font-bold">
                                    {d.count} appts
                                </div>
                            </foreignObject>
                        </g>
                    );
                })}
            </svg>
            {/* X-Axis Labels */}
            <div className="flex justify-between mt-2 text-[10px] text-slate-400 uppercase font-mono" style={{ paddingLeft: `${(padLeft / width) * 100}%`, paddingRight: `${(padRight / width) * 100}%` }}>
                {data.map((d, i) => (
                    <span key={i}>{fmtChartDate(d.date, data.length)}</span>
                ))}
            </div>
        </div>
    );
}
